import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Paperclip, X, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/authFetch";

const TYPES = [
  { value: "bug", label: "🐛 Bug / dysfonctionnement" },
  { value: "suggestion", label: "💡 Suggestion" },
  { value: "amelioration_ux", label: "✨ Amélioration UX" },
  { value: "idee", label: "🎯 Idée" },
  { value: "autre", label: "Autre" },
];

interface Props {
  floating?: boolean;
  className?: string;
}

export default function FeedbackForm({ floating = false, className }: Props) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("suggestion");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [pending, setPending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  function reset() {
    setType("suggestion");
    setMessage("");
    setFile(null);
    setPending(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setPending(true);
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("message", message.trim());
      fd.append("contexte_url", window.location.pathname);
      if (file) fd.append("fichier", file);
      const res = await authFetch("/api/feedback", { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur lors de l'envoi");
      }
      toast({ title: "Merci pour votre feedback !", description: "Notre équipe en prendra connaissance." });
      setOpen(false);
      reset();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setPending(false);
    }
  }

  const btn = floating ? (
    <button
      onClick={() => setOpen(true)}
      title="Donner un feedback"
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-4 py-2.5 shadow-lg hover:shadow-xl hover:scale-105 transition-all text-sm font-medium ${className ?? ""}`}
      data-testid="button-feedback-floating"
    >
      <MessageSquare className="w-4 h-4" />
      Feedback
    </button>
  ) : (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      className={className}
      data-testid="button-feedback-open"
    >
      <MessageSquare className="w-4 h-4 mr-2" />
      Donner un feedback
    </Button>
  );

  return (
    <>
      {btn}
      <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              Donner un feedback
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={type || "suggestion"} onValueChange={setType}>
                <SelectTrigger data-testid="select-feedback-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Message <span className="text-destructive">*</span></Label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
                placeholder="Décrivez votre remarque, bug ou idée…"
                required
                className="resize-none"
                data-testid="textarea-feedback-message"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 cursor-pointer">
                <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
                Capture d'écran (optionnel)
              </Label>
              {file ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 rounded-lg px-3 py-2">
                  <span className="flex-1 truncate">{file.name}</span>
                  <button type="button" onClick={() => setFile(null)}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => fileRef.current?.click()}
                  data-testid="button-feedback-attach"
                >
                  Joindre une image ou PDF
                </Button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                className="hidden"
                onChange={e => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>
                Annuler
              </Button>
              <Button type="submit" disabled={pending || !message.trim()} data-testid="button-feedback-submit">
                {pending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Envoyer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
